import { MCDANIEL_SEARCH_FOLDER_NAME } from "constants/settings.constants";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { EntityKind } from "models/entityKind";
import { IGroupByListColumn, IGroupByListItem } from "models/groupBy";

import type { GroupByT } from "./GroupBy";

// ─── Constants ───────────────────────────────────────────────────────────────
export const MINIMUM_SEARCH_LENGTH = 2;
export const DEFAULT_RESCAT = "P+PDP";
export const DEFAULT_PDEN_SOURCE = 100;
export const DEFAULT_SELECTED_TAB = "fields";
export const DEFAULT_METADATA_FILTERS: Readonly<MetadataFiltersT> = {
  source: "All Sources",
  type: "XDA",
  radius: "Gross"
} as const;

// ─── Props type (from parent) ────────────────────────────────────────────────
export type PropsT = Omit<GroupByT, "children" | "placement">;

// ─── Metadata filters ────────────────────────────────────────────────────────
export type MetadataFiltersT = { [key: string]: string };

// ─── Shapefile selection ─────────────────────────────────────────────────────
/**
 * The shapefile the picker is browsing, kept here rather than in the category
 * that shows it: that category is thrown away every time the user reads another
 * one and built again on the way back, and the selection has to outlive it. It
 * belongs to the picker, so each picker remembers its own and a closed one
 * remembers nothing.
 *
 * The project it was picked in rides along, so a selection is never handed back
 * to a project it does not belong to.
 */
export type SelectedShapefileT = {
  readonly projectId: Maybe<string>;
  readonly shapefileId: string;
  /** A GeoTIFF has no boundary to filter on, only a raster to read. */
  readonly isGeoTiff: boolean;
};

// ─── State ───────────────────────────────────────────────────────────────────
export interface GroupByState {
  readonly props: Maybe<PropsT>;
  readonly allColumns: IGroupByListColumn[];
  readonly categoryList: IGroupByListItem[];
  readonly categoryColors: Readonly<Record<string, string>>;
  readonly selectedCategory: Maybe<IGroupByListItem>;
  readonly selectedField: Maybe<IGroupByListColumn>;
  readonly selectedShapefile: Maybe<SelectedShapefileT>;
  readonly selectedProduct: Maybe<string>;
  readonly filterText: string;
  readonly isFiltering: boolean;
  readonly isForecastToggleOn: boolean;
  readonly selectedForecastFolder: string;
  readonly selectedForecastFolderName: string;
  readonly selectedRescat: string;
  readonly selectedPdenSource: number;
  readonly selectedStartDate: Maybe<Date>;
  readonly selectedEndDate: Maybe<Date>;
  readonly selectedMetadataFilters: Readonly<MetadataFiltersT>;
  readonly checkedFields: readonly string[];
  readonly customFieldsEditToggle: boolean;
  readonly selectedTab: string;
  readonly entityKind: EntityKind;
  readonly hoveredField: Maybe<IGroupByListColumn>;
}

// ─── Actions ─────────────────────────────────────────────────────────────────
export interface GroupByActions {
  readonly setProps: (props: PropsT) => void;
  readonly setEntityKind: (entityKind: EntityKind) => void;
  readonly updateCategoryList: (items: IGroupByListItem[]) => void;
  readonly setSelectedCategory: (category: IGroupByListItem) => void;
  readonly setSelectedField: (field: IGroupByListColumn) => void;
  readonly setSelectedShapefile: (selected: Maybe<SelectedShapefileT>) => void;
  readonly setSelectedProduct: (product: string) => void;
  readonly setSelectedPdenSource: (source: number) => void;
  readonly setSelectedTab: (tab: string) => void;
  readonly setFilterText: (text: string) => void;
  readonly setForecastToggle: (on: boolean) => void;
  readonly setSelectedForecastFolder: (folderId: string) => void;
  readonly setSelectedForecastFolderName: (name: string) => void;
  readonly setSelectedRescat: (rescat: string) => void;
  readonly setSelectedStartDate: (date: Maybe<Date>) => void;
  readonly setSelectedEndDate: (date: Maybe<Date>) => void;
  readonly setMetadataFilter: (key: string, value: string) => void;
  readonly setCheckedFields: (ids: readonly string[]) => void;
  readonly setCustomFieldsEditToggle: (on: boolean) => void;
  readonly enableCustomFieldsEdit: () => void;
  readonly disableCustomFieldsEdit: () => void;
  readonly setHoveredField: (field: Maybe<IGroupByListColumn>) => void;
  readonly reset: () => void;
}

// ─── Combined store type ─────────────────────────────────────────────────────
export type GroupByStore = GroupByState & GroupByActions;

// ─── Initial state factory ───────────────────────────────────────────────────
export const createInitialState = (overrides?: Partial<GroupByState>): GroupByState => ({
  props: null,
  allColumns: [],
  categoryList: [],
  categoryColors: {},
  selectedCategory: null,
  selectedField: null,
  selectedShapefile: null,
  selectedProduct: null,
  filterText: "",
  isFiltering: false,
  isForecastToggleOn: true,
  selectedForecastFolder: MCDANIEL_SEARCH_FOLDER_NAME,
  selectedForecastFolderName: MCDANIEL_SEARCH_FOLDER_NAME,
  selectedRescat: DEFAULT_RESCAT,
  selectedPdenSource: DEFAULT_PDEN_SOURCE,
  selectedStartDate: null,
  selectedEndDate: null,
  selectedMetadataFilters: { ...DEFAULT_METADATA_FILTERS },
  checkedFields: [],
  customFieldsEditToggle: false,
  selectedTab: DEFAULT_SELECTED_TAB,
  entityKind: EntityKind.Well,
  hoveredField: null,
  ...overrides
});

// ─── Store factory ───────────────────────────────────────────────────────────
export const createGroupByStore = (overrides?: Partial<GroupByState>) => {
  const initial = createInitialState(overrides);

  return createStore<GroupByStore>()(
    subscribeWithSelector((set) => ({
      ...initial,

      // ── Initialization ──────────────────────────────────────────────
      setProps: (props) => set({ props }),
      setEntityKind: (entityKind) => set({ entityKind }),

      // ── Category list ───────────────────────────────────────────────
      updateCategoryList: (items) => {
        const allColumns = items.flatMap((cat) => cat.columns);
        const categoryColors = Object.fromEntries(
          items.map((item) => [item.name, item.hexColor])
        );
        set({ categoryList: items, allColumns, categoryColors });
      },

      // ── Selections ──────────────────────────────────────────────────
      setSelectedCategory: (category) => set({ selectedCategory: category }),
      setSelectedField: (field) => set({ selectedField: field }),
      setSelectedShapefile: (selected) => set({ selectedShapefile: selected }),
      setSelectedProduct: (product) => set({ selectedProduct: product }),
      setSelectedPdenSource: (source) => set({ selectedPdenSource: source }),
      setSelectedTab: (tab) => set({ selectedTab: tab }),

      // ── Search / filter ─────────────────────────────────────────────
      setFilterText: (text) =>
        set({ filterText: text, isFiltering: text.length >= MINIMUM_SEARCH_LENGTH }),

      // ── Forecast ────────────────────────────────────────────────────
      setForecastToggle: (on) => set({ isForecastToggleOn: on }),
      setSelectedForecastFolder: (id) => set({ selectedForecastFolder: id }),
      setSelectedForecastFolderName: (name) => set({ selectedForecastFolderName: name }),
      setSelectedRescat: (rescat) => set({ selectedRescat: rescat }),
      setSelectedStartDate: (date) => set({ selectedStartDate: date }),
      setSelectedEndDate: (date) => set({ selectedEndDate: date }),

      // ── Metadata filters ────────────────────────────────────────────
      setMetadataFilter: (key, value) =>
        set((s) => ({
          selectedMetadataFilters: { ...s.selectedMetadataFilters, [key]: value }
        })),

      // ── Custom field editing ────────────────────────────────────────
      setCheckedFields: (ids) => set({ checkedFields: ids }),
      setCustomFieldsEditToggle: (on) => set({ customFieldsEditToggle: on }),
      enableCustomFieldsEdit: () => set({ customFieldsEditToggle: true }),
      disableCustomFieldsEdit: () => set({ customFieldsEditToggle: false }),

      // ── Hover ───────────────────────────────────────────────────────
      setHoveredField: (field) => set({ hoveredField: field }),

      // ── Reset ───────────────────────────────────────────────────────
      reset: () => set(createInitialState(overrides))
    }))
  );
};

export type GroupByStoreApi = ReturnType<typeof createGroupByStore>;
